import clsx from 'clsx';
import React from 'react';

export type MetricProgressVariant = 'primary' | 'secondary' | 'accent' | 'blue' | 'pink' | 'zinc';

/**
 * Static gradient classes (must live in a .tsx file for Tailwind content scanning).
 * Light-to-dark (500 → 600) so the fill edge against the neutral track stays visible.
 */
export const METRIC_PROGRESS_GRADIENT: Record<MetricProgressVariant, string> = {
  primary: 'bg-gradient-to-r from-primary-500 to-primary-600',
  secondary: 'bg-gradient-to-r from-secondary-500 to-secondary-600',
  accent: 'bg-gradient-to-r from-accent-500 to-accent-600',
  blue: 'bg-gradient-to-r from-blue-500 to-blue-600',
  pink: 'bg-gradient-to-r from-pink-500 to-pink-600',
  zinc: 'bg-gradient-to-r from-zinc-400 dark:from-zinc-600 to-zinc-500'
};

type MetricProgressBarProps = {
  /** Fill percentage, 0–100 (clamped). */
  value: number;
  variant?: MetricProgressVariant;
  size?: 'sm' | 'md';
  className?: string;
};

const SIZE_CLASSES = {
  sm: 'h-1',
  md: 'h-1.5'
} as const;

/**
 * Static gradient progress bar for analytics metrics.
 * Matches subscription seat bars and workspace upload progress styling.
 */
export const MetricProgressBar: React.FC<MetricProgressBarProps> = ({
  value,
  variant = 'primary',
  size = 'md',
  className
}) => {
  const clamped = Math.max(0, Math.min(100, value));
  const fillClass = METRIC_PROGRESS_GRADIENT[variant];

  return (
    <div
      className={clsx(
        'w-full overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-700',
        SIZE_CLASSES[size],
        className
      )}
    >
      {clamped > 0 && (
        <div
          className={clsx('h-full min-w-[3px] rounded-full', fillClass)}
          style={{ width: `${clamped}%` }}
        />
      )}
    </div>
  );
};
